<script setup lang="ts">
import { computed } from 'vue'
import CollapsibleCard from './CollapsibleCard.vue'
import type { UserOtpConfig } from '../../api/client'
import type { OtpBarState } from '../../composables/useSignalPipeline'

const props = defineProps<{
  otpConfigs: UserOtpConfig[]
  otpBar: (t: string) => OtpBarState | null
  otpByType: (t: string) => UserOtpConfig | undefined
  otpDayType: 'buy' | 'sell' | null
  otpDivergence: { committed: string; live: string } | null
  otpExemptActive: boolean
  vixDelta: number | null
}>()

// Sub-project B: actual execution flags for the four strategies are not yet
// wired through the panel — the retired hedgeActive/overlayActive/scActive/
// lcActive computeds always returned false, so the row config below hardcodes
// that literal rather than threading dead computeds through as props.
const rows = [
  { type: 'hedge', label: 'Hedging', inst: 'LQQS', active: false, dayLabel: 'BUY days' },
  { type: 'overlay', label: 'Overlay', inst: 'LQQ3', active: false, dayLabel: 'SELL days' },
  { type: 'short_conv', label: 'Short Conv', inst: 'LQQS', active: false, dayLabel: 'SELL days' },
  { type: 'long_conv', label: 'Long Conv', inst: 'LQQ3', active: false, dayLabel: 'BUY days' },
]

const summary = computed(() => {
  const delta = props.vixDelta != null
    ? `${props.vixDelta >= 0 ? '+' : ''}${props.vixDelta.toFixed(1)}%`
    : '—'
  // Count the rendered rows, not otpConfigs — the table always shows all
  // four strategies (a row with no config just renders an idle bar cell),
  // so a user with a partial config set must not see e.g. "2 strategies"
  // collapsed but 4 rows expanded.
  return `${rows.length} strategies · VIX Δ ${delta}`
})

const alert = computed(() => props.otpDivergence != null)
</script>

<template>
  <CollapsibleCard title="OTP strategies" testid="otp-card" :summary="summary" :alert="alert">
    <!-- Hedging / Overlay / Short Conviction table — ported verbatim from
         SignalPanel.vue lines 1216-1269. -->
    <table class="mit-table">
      <thead>
        <tr>
          <th>Strategy</th>
          <th>Trigger</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="otp in rows" :key="otp.type">
          <td>{{ otp.label }} <span class="mit-inst">{{ otp.inst }}</span></td>
          <td>{{ otp.dayLabel }}</td>
          <td v-if="otp.active" class="mit-active">ACTIVE</td>
          <td v-else class="mit-bar-cell" :class="{ 'mit-greyed': otpBar(otp.type)?.greyed }">
            <template v-if="otpBar(otp.type)">
              <div class="mit-bar-wrap">
                <div class="mit-bar-caret" :style="{ left: otpBar(otp.type)!.thrPct + '%' }">{{ Math.abs(otpByType(otp.type)?.triggerThreshold ?? 0) }}%</div>
                <div class="mit-bar-track">
                  <div v-if="!otpBar(otp.type)!.greyed" class="mit-bar-fill" :class="{ 'mit-bar-away': !otpBar(otp.type)!.movingToward }" :style="{ width: otpBar(otp.type)!.fillPct + '%' }"></div>
                  <div class="mit-bar-threshold" :style="{ left: otpBar(otp.type)!.thrPct + '%' }"></div>
                </div>
              </div>
              <span class="mit-bar-label" :class="otpBar(otp.type)!.greyed ? 'mit-bar-label-away' : (otpBar(otp.type)!.movingToward ? '' : 'mit-bar-label-away')">{{ otpBar(otp.type)!.label }}</span>
              <span v-if="!otpBar(otp.type)!.enabled" class="mit-off-badge">OFF</span>
            </template>
            <template v-else>
              <span class="mit-idle">--</span>
            </template>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr v-if="otpExemptActive">
          <td colspan="3" class="mit-xd-exempt">
            ⚡ XD-exempt: BGA on HOLD (dividend day) but OTPs are <strong>armed as a {{ otpDayType?.toUpperCase() }} day</strong> based on signal direction
          </td>
        </tr>
        <tr v-if="otpDivergence">
          <td colspan="3" class="mit-divergence">
            ⚠ Committed <strong>{{ otpDivergence.committed.toUpperCase() }}</strong> at cutoff · live signal now <strong>{{ otpDivergence.live.toUpperCase() }}</strong> — OTPs honour the committed decision
          </td>
        </tr>
        <tr v-if="vixDelta != null">
          <td colspan="3" class="mit-delta">
            VIX Δ today: {{ vixDelta >= 0 ? '+' : '' }}{{ vixDelta.toFixed(1) }}%
          </td>
        </tr>
      </tfoot>
    </table>
  </CollapsibleCard>
</template>

<style scoped>
/* ---- Mitigation table — ported verbatim from SignalPanel.vue ---- */
.mit-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  margin-top: 2px;
}
.mit-table th {
  text-align: left;
  font-size: 0.65rem;
  color: #6e7681;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 6px;
  border-bottom: 1px solid #21262d;
}
.mit-table td {
  padding: 4px 6px;
  color: #8b949e;
}
.mit-table tbody tr { border-bottom: 1px solid #161b22; }
.mit-inst { color: #6e7681; font-size: 0.75rem; font-family: 'JetBrains Mono', monospace; }
.mit-active { color: #3fb950; font-weight: 600; }
.mit-idle { color: #6e7681; }
.mit-delta { font-size: 0.8rem; color: #8b949e; padding-top: 4px; }
.mit-xd-exempt {
  font-size: 0.75rem;
  color: #e6a23c;
  background: rgba(230,162,60,0.10);
  border-left: 2px solid #e6a23c;
  padding: 5px 8px;
  margin-top: 4px;
  border-radius: 2px;
}
.mit-xd-exempt strong { color: #ffb84d; }
.mit-divergence {
  font-size: 0.75rem;
  color: #8b949e;
  background: rgba(139,148,158,0.08);
  border-left: 2px solid #8b949e;
  padding: 5px 8px;
  margin-top: 4px;
  border-radius: 2px;
}
.mit-divergence strong { color: #c9d1d9; }
.mit-bar-cell { display: flex; align-items: center; gap: 4px; padding: 4px 6px; }
.mit-bar-wrap { position: relative; flex: 1; min-width: 40px; padding-top: 10px; }
.mit-bar-caret { position: absolute; top: 0; transform: translateX(-50%); font-size: 0.5rem; color: #f85149; font-family: 'JetBrains Mono', monospace; white-space: nowrap; }
.mit-bar-caret::after { content: '▼'; display: block; text-align: center; font-size: 0.4rem; line-height: 1; }
.mit-bar-track { position: relative; height: 6px; background: #21262d; border-radius: 3px; }
.mit-bar-fill { height: 100%; background: #3fb950; border-radius: 3px; max-width: 100%; }
.mit-bar-fill.mit-bar-away { background: #6e7681; }
.mit-bar-threshold { position: absolute; top: -2px; width: 2px; height: 10px; background: #f85149; border-radius: 1px; }
.mit-bar-label { font-size: 0.65rem; font-family: 'JetBrains Mono', monospace; color: #3fb950; white-space: nowrap; }
.mit-bar-label-away { color: #6e7681; }
.mit-wz-block { font-size: 0.55rem; font-family: 'JetBrains Mono', monospace; color: #f85149; background: #3d1216; padding: 0 3px; border-radius: 2px; white-space: nowrap; }
.mit-greyed { opacity: 0.3; }
.mit-off-badge { font-size: 0.55rem; color: #6e7681; background: #21262d; padding: 0 3px; border-radius: 2px; white-space: nowrap; }
</style>
